// Library methods

// Utils
import { toast } from 'react-toastify';
import moment from 'moment';
import { takeLatest, put, call } from 'redux-saga/effects';
import { POST, GET } from '../../../utils/fetchWrappers';

// API URLs
import {
    API_FETCH_TRANSACTION,
    API_GENERATE_REPORT,
    API_DOWNLOAD_REPORT,
    API_GET_ACTIVE_REPORT,
    API_TRANSACTION_STATUS,
    API_FETCH_REFUND_TXN,
    API_GET_ACTIVE_REPORT_CUSTOM,
} from '../../../utils/API/apiUrls';

// API Status Codes
import { apiStatusCodes } from '../../../utils/API/apiStatusCodes';

// Redux Actions
import * as loaderActions from '../../../redux/common/loader/loader.actions';
import transactionMockData from '../mockData/transactions.json';
import * as transactionsActions from './transactions.actions';

// Constants
import {
    FETCH_TRANSACTIONS_REQUEST,
    GENERATE_REPORT_REQUEST,
    GET_ACTIVE_REPORT_REQUEST,
    DOWNLOAD_REPORT_REQUEST,
    FETCH_TRANSACTIONS_STATUS_REQUEST,
    GET_REFUND_TXN_REQUEST,
} from './transactions.constants';

const TID_MOCK = false;

const getRequestData = payload => {
    const request = {
        pageNo: payload.pageIndex || 1,
        pageSize: payload.pageSize || 10,
        transactionView: 'TXN',
    };
    if (payload.createdOn) {
        request.startDateTime = moment(payload.createdOn.startDate)
            .startOf('day')
            .format('YYYY-MM-DD HH:mm:ss');
        request.endDateTime = moment(payload.createdOn.endDate)
            .endOf('day')
            .format('YYYY-MM-DD HH:mm:ss');
    }

    if (payload.merchantIds && payload.merchantIds.length) {
        request.merchantIds = payload.merchantIds;
    }

    if (payload.searchKey) {
        request.searchKey = payload.searchKey;
    }

    if (payload.searchValue) {
        request.searchValue = payload.searchValue;
    }

    if (payload.orderId) {
        request.orderId = payload.orderId;
    }

    if (payload.terminalId) {
        request.terminalId = payload.terminalId;
    }

    if (payload.transactionReferenceNumber) {
        request.transactionReferenceNumber = payload.transactionReferenceNumber;
    }
    if (payload.merchantOrderId) {
        request.merchantOrderId = payload.merchantOrderId;
    }

    if (payload.minAmt) {
        request.minAmt = payload.minAmt;
    }
    if (payload.isLive !== undefined) {
        request.isLive = payload.isLive;
    }
    if (payload.transactionModes) {
        request.transactionModes = [payload.transactionModes];
    }
    if (payload.maxAmt) {
        request.maxAmt = payload.maxAmt;
    }
    if (payload.transactionTypes && payload.transactionTypes.length) {
        const { transactionTypes } = payload;
        request.transactionTypes = transactionTypes;
    }
    if (payload.transactionStates && payload.transactionStates.length) {
        const { transactionStates } = payload;
        request.transactionStates = transactionStates;
    }
    return request;
};

const getRequestDataFile = payload => {
    const request = {};
    if (payload.createdOn) {
        request.startDateTime = moment(payload.createdOn.startDate)
            .startOf('day')
            .format('YYYY-MM-DD HH:mm:ss');
        request.endDateTime = moment(payload.createdOn.endDate)
            .endOf('day')
            .format('YYYY-MM-DD HH:mm:ss');
    }
    if (payload.merchantIds && payload.merchantIds.length) {
        request.merchantIds = payload.merchantIds;
    }
    if (payload.reportFileName) {
        request.reportFileName = payload.reportFileName;
    }
    if (payload.orderId) {
        request.orderId = payload.orderId;
    }
    if (payload.minAmt) {
        request.minAmt = payload.minAmt;
    }
    if (payload.maxAmt) {
        request.maxAmt = payload.maxAmt;
    }
    if (payload.transactionTypes && payload.transactionTypes.length) {
        const { transactionTypes } = payload;
        request.transactionTypes = transactionTypes;
    }
    if (payload.transactionStates && payload.transactionStates.length) {
        const { transactionStates } = payload;
        request.transactionStates = transactionStates;
    }
    return request;
};

export function* fetchTransactionsDetails(actions) {
    yield put(transactionsActions.showLoader());
    try {
        const { payload } = actions;
        const request = getRequestData(payload);
        let data = {};

        if (TID_MOCK) {
            data = transactionMockData;
        } else {
            const res = yield call(POST, API_FETCH_TRANSACTION, request);
            data = res.data;
        }

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(transactionsActions.fetchTransactionsSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                transactionsActions.fetchTransactionsError({
                    error: data,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(
            transactionsActions.fetchTransactionsError({
                error: data,
            })
        );
    }
    yield put(transactionsActions.hideLoader());
}

export function* getActiveReportDetails(actions) {
    const { hideLoader } = actions;
    if (!hideLoader) {
        yield put(loaderActions.showLoader());
    }
    try {
        const { data } = yield call(
            GET,
            actions?.payload?.reportType
                ? API_GET_ACTIVE_REPORT_CUSTOM(actions.payload.reportType)
                : API_GET_ACTIVE_REPORT
        );
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(transactionsActions.getActiveReportSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                transactionsActions.getActiveReportError({
                    error: data,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(
            transactionsActions.getActiveReportError({
                error: data,
            })
        );
    }
    if (!hideLoader) {
        yield put(loaderActions.hideLoader());
    }
}

export function* generateTransactionReport(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;

        let request = {};
        // for settlement tab
        if (payload.reportType === 'MSR') {
            request = payload;
        } else if (payload.reportType === 'UNIFIED_MSR' || payload.reportType === 'EDI_REPORT') {
            request = payload;
        } else {
            // for txn tab
            request = getRequestDataFile(payload);
            request.reportType = 'TRANSACTIONS';
        }

        const { data } = yield call(POST, API_GENERATE_REPORT, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(transactionsActions.generateReportSuccess(data));
        } else if (data && data.errorMessage) {
            const errorMessage = data.errorMessage || '';
            yield put(
                transactionsActions.generateReportError({
                    error: errorMessage || '',
                })
            );
            if (errorMessage) {
                toast.error(errorMessage);
            }
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data.errorMessage || '';
        yield put(
            transactionsActions.generateReportError({
                error: errorMessage || '',
            })
        );
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }
    yield put(loaderActions.hideLoader());
}

export function* downloadReportRequest(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const request = {
            requestId: payload.requestId,
        };
        const { data } = yield call(POST, API_DOWNLOAD_REPORT, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(transactionsActions.downloadReportSuccess(data.fileUrl));
            if (data.fileUrl) {
                const link = document.createElement('a');
                link.href = data.fileUrl;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } else if (data && data.errorMessage) {
            yield put(
                transactionsActions.downloadReportError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(
            transactionsActions.downloadReportError({
                error: data,
            })
        );
    }
    yield put(loaderActions.hideLoader());
}

export function* fetchTransactionsStatusDetails(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_TRANSACTION_STATUS, payload);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(transactionsActions.fetchTransactionsStatusSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                transactionsActions.fetchTransactionsStatusError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(
            transactionsActions.fetchTransactionsStatusError({
                error: data,
            })
        );
    }
    yield put(loaderActions.hideLoader());
}

export function* fetchRefundTxnDetails(actions) {
    // yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_FETCH_REFUND_TXN, payload);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(transactionsActions.getRefundTxnSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                transactionsActions.getRefundTxnError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(
            transactionsActions.getRefundTxnError({
                error: data,
            })
        );
    }
    // yield put(loaderActions.hideLoader());
}

/**
 * WATCHER SAGA
 */
export function* transactionsSaga() {
    yield takeLatest(FETCH_TRANSACTIONS_REQUEST, fetchTransactionsDetails);
    yield takeLatest(GET_REFUND_TXN_REQUEST, fetchRefundTxnDetails);
    yield takeLatest(GENERATE_REPORT_REQUEST, generateTransactionReport);
    yield takeLatest(GET_ACTIVE_REPORT_REQUEST, getActiveReportDetails);
    yield takeLatest(DOWNLOAD_REPORT_REQUEST, downloadReportRequest);
    yield takeLatest(
        FETCH_TRANSACTIONS_STATUS_REQUEST,
        fetchTransactionsStatusDetails
    );
}
